/**
 * Stytch SMS OTP Creation Hook
 *
 * This hook provides functionality for creating TOTP (Time-based One-Time Password)
 * authentication using the Stytch B2B headless client.
 */
import { useCallback } from 'react';
import { getStytchClient } from '../../../../adapters/react/client/headless-client';
import { STYTCH_DEFAULT_SESSION_DURATION_MINUTES } from '../../../../core/session-durations';
export function useStytchStartSSOFlow() {
    return useCallback(async (data) => {
        const stytch = getStytchClient();
        if (!stytch) {
            return {
                success: false,
                error: 'Stytch client not ready',
                data: null,
            };
        }
        try {
            await stytch.sso.start({
                connection_id: data.connectionId,
                login_redirect_url: data.loginRedirectUrl,
                signup_redirect_url: data.signupRedirectUrl,
            });
            // If we reach here, the redirect was successful
            // The user will be redirected to Stytch's SSO page
            return {
                success: true,
                data: { redirected: true },
            };
        }
        catch (error) {
            const err = error;
            return {
                success: false,
                error: err.error_message || err.message || 'Failed to start SSO flow',
                data: error,
            };
        }
    }, []);
}
export function useStytchGetSSOConnections() {
    return useCallback(async () => {
        const stytch = getStytchClient();
        if (!stytch) {
            return {
                success: false,
                error: 'Stytch client not ready',
                data: null,
            };
        }
        try {
            const result = await stytch.sso.getConnections();
            return {
                success: true,
                requestId: result.request_id,
                statusCode: result.status_code,
                samlConnections: result.saml_connections,
                oidcConnections: result.oidc_connections,
                externalConnections: result.external_connections,
            };
        }
        catch (error) {
            const err = error;
            return {
                success: false,
                error: err.error_message || err.message || 'Failed to get SSO flow',
                data: error,
            };
        }
    }, []);
}
export function useStytchDiscoverSSOConnections() {
    return useCallback(async (data) => {
        const stytch = getStytchClient();
        if (!stytch) {
            return {
                success: false,
                error: 'Stytch client not ready',
                data: null,
            };
        }
        try {
            const result = await stytch.sso.discoverConnections(data.emailAddress);
            return {
                success: true,
                requestId: result.request_id,
                statusCode: result.status_code,
                connections: result.connections,
            };
        }
        catch (error) {
            const err = error;
            return {
                success: false,
                error: err.error_message || err.message || 'Failed to discover SSO connections',
                data: error,
            };
        }
    }, []);
}
export function useStytchDeleteSSOConnection() {
    return useCallback(async (data) => {
        const stytch = getStytchClient();
        if (!stytch) {
            return {
                success: false,
                error: 'Stytch client not ready',
                data: null,
            };
        }
        try {
            const result = await stytch.sso.deleteConnection(data.connectionId);
            return {
                success: true,
                requestId: result.request_id,
                statusCode: result.status_code,
                connectionId: result.connection_id,
            };
        }
        catch (error) {
            const err = error;
            return {
                success: false,
                error: err.error_message || err.message || 'Failed to delete SSO connection',
                data: error,
            };
        }
    }, []);
}
export function useStytchAuthenticateSSO() {
    return useCallback(async (data) => {
        const stytch = getStytchClient();
        if (!stytch) {
            return {
                success: false,
                error: 'Stytch client not ready',
                data: null,
            };
        }
        try {
            const result = await stytch.sso.authenticate({
                sso_token: data.ssoToken,
                session_duration_minutes: data.sessionDurationMinutes || STYTCH_DEFAULT_SESSION_DURATION_MINUTES,
                locale: data.locale,
            });
            return {
                success: true,
                statusCode: result.status_code,
                requestId: result.request_id,
                memberId: result.member_id,
                organizationId: result.organization?.organization_id,
                member: result.member,
                organization: result.organization,
                sessionToken: result.session_token,
                sessionJwt: result.session_jwt,
                intermediateSessionToken: result.intermediate_session_token,
                memberAuthenticated: result.member_authenticated,
                mfaRequired: result.mfa_required,
                memberSession: result.member_session,
                data: result,
            };
        }
        catch (error) {
            const err = error;
            return {
                success: false,
                error: err.error_message || err.message || 'Failed to authenticate SSO',
                data: error,
            };
        }
    }, []);
}
